import { ICopilotPrompt } from "../../interfaces/ICopilotPrompt";
import { ICopilotScope } from "./copilotScope";

/**
 * The prompt library's rules: matching a library name, reading the API's prompts and choosing the ones that apply.
 */

/**
 * Whether a site's library matches a prompt's library name: the same name, ignoring case, or a
 * longer name that starts with it ("Permanent dossier" for "Permanent"). Ported from the MVP.
 */
export const matchesLibrary = (libraryName: string, wanted: string): boolean => {
  const name: string = (libraryName ?? "").trim().toLowerCase();
  const want: string = (wanted ?? "").trim().toLowerCase();
  return want.length > 0 && (name === want || name.indexOf(`${want} `) === 0);
};

// ── Phase 3: the prompt library (spec 2026-10-13-copilot-chat-design.md §5.5 "Prompts") ──
// ES5 rules apply (Global Constraints): no Object.entries, no iterable spreads.

/**
 * Reads GET api/Copilot/Prompts. The API leaves out null members, so a missing guidance or language
 * is null and missing default libraries are none. A missing requiresScope counts as true: the prompt
 * then stays away from All documents rather than running without a dossier.
 */
export function normalizeCopilotPrompts(raw: unknown): ICopilotPrompt[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const prompts: ICopilotPrompt[] = [];
    raw.forEach((item: any) => {
        if (!item || typeof item.id !== "number" || typeof item.title !== "string" || typeof item.text !== "string") {
            return;
        }
        const title = item.title.trim();
        const text = item.text.trim();
        if (!title || !text) {
            return;
        }
        prompts.push({
            id: item.id,
            title,
            text,
            guidance: typeof item.guidance === "string" && item.guidance.trim() ? item.guidance : null,
            defaultLibraries: Array.isArray(item.defaultLibraries)
                ? item.defaultLibraries
                    .filter((name: unknown) => typeof name === "string" && (name as string).trim() !== "")
                    .map((name: string) => name.trim())
                : [],
            language: typeof item.language === "string" && item.language.trim() ? item.language.trim().toLowerCase() : null,
            requiresScope: item.requiresScope !== false,
            isGlobal: item.isGlobal === true,
        });
    });
    return prompts;
}

/** The prompts' language when none is in the UI language (spec §5.5, amended 2026-10-14). */
const FALLBACK_PROMPT_LANGUAGE = "en";

/**
 * The prompts to offer here, in the API's order:
 * - those in the UI language and those for every language (language null);
 * - when no prompt in the whole list is in the UI language (German, with the Dutch and English
 *   seed), the English ones instead, still with the every-language ones;
 * - minus the ones that need a location while the scope is All documents (or not known yet), unless
 *   options.keepScoped: the prompt dropdown lists them everywhere, so a chosen prompt never disappears
 *   when Search in changes, and shows the POC's hint at All documents instead (spec 2026-10-16 §3, §5).
 * The language is decided over the whole list, before the location filter, so moving to All
 * documents never switches the offered prompts to English.
 */
export function applicablePrompts(prompts: ICopilotPrompt[], uiLanguage: string, scope: ICopilotScope | null | undefined, options?: { keepScoped?: boolean }): ICopilotPrompt[] {
    const all = prompts || [];
    const uiCode = (uiLanguage || "").trim().toLowerCase().substring(0, 2);
    const language = all.some(prompt => prompt.language === uiCode) ? uiCode : FALLBACK_PROMPT_LANGUAGE;
    const atAllDocuments = options?.keepScoped !== true && (!scope || scope.kind === "all");
    return all.filter(prompt =>
        (!prompt.language || prompt.language === language) && !(atAllDocuments && prompt.requiresScope));
}
